import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/* the room left above a section reached by its link (#videos), as the centre
   pages' own section menu leaves */
const HASH_OFFSET = 80;

/**
 * Brings the section a link names (#videos) into view. The page renders, then
 * loads its text and images, after the navigation, so the section is looked
 * for until it appears and then held in place while the content above it
 * settles: until it has stayed put for a moment, or the visitor scrolls.
 */
function followHash(id: string) {
  let stopped = false;
  let stillSince = Date.now();
  const started = Date.now();
  const stop = () => { stopped = true; };
  const events = ["wheel", "touchstart", "keydown", "mousedown"];
  events.forEach((e) => window.addEventListener(e, stop, { passive: true }));
  let timer = 0;
  const tick = () => {
    if (stopped || Date.now() - started > 12000) return;
    const el = document.getElementById(id);
    if (el) {
      // how far the section is from where it should be; while the page is
      // still too short to scroll that far, this stays off and it tries again
      const off = Math.round(el.getBoundingClientRect().top - HASH_OFFSET);
      if (Math.abs(off) > 2) {
        window.scrollTo(0, window.scrollY + off);
        stillSince = Date.now();
      } else if (Date.now() - stillSince > 1500) {
        return;
      }
    }
    timer = window.setTimeout(tick, 150);
  };
  timer = window.setTimeout(tick, 0);
  return () => {
    stopped = true;
    window.clearTimeout(timer);
    events.forEach((e) => window.removeEventListener(e, stop));
  };
}

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  const firstLoad = useRef(true);

  useEffect(() => {
    // Explicitly set scroll restoration to auto to allow the browser
    // to handle the scroll position when using back/forward buttons.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "auto";
    }
  }, []);

  useEffect(() => {
    const first = firstLoad.current;
    firstLoad.current = false;
    // A link to a section of a page (on arrival, or opened directly):
    // to that section.
    if (hash && (navType !== "POP" || first)) {
      if (navType !== "POP") window.scrollTo(0, 0);
      return followHash(decodeURIComponent(hash.slice(1)));
    }
    // Only scroll to top on 'PUSH' (forward) or 'REPLACE' navigation.
    // On 'POP' (Back/Forward buttons), we let the browser restore the position.
    if (navType === "PUSH" || navType === "REPLACE") {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navType]);

  return null;
};

export default ScrollToTop;
